import { Button } from "@dropbox/dig-components/buttons";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine, EditLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { PeopleBreadcrumbs } from "@mirage/mosaics/BaseProfilePage/Breadcrumb";
import { userProfileSettingsAtom } from "@mirage/mosaics/UserProfilePage/atoms";
import {
  canUserEditUserProfile,
  convertPeopleRecommendationToBasicUserInfo,
  getEditUserProfilePageHref,
  getUserProfilePageHref,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import {
  useOrgChart,
  usePersonFromOrgChart,
} from "@mirage/service-people/hooks";
import { getReportingLine } from "@mirage/service-people/service/utils";
import i18n from "@mirage/translations";
import { useSetAtom } from "jotai";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";
import styles from "./UserProfileHeader.module.css";

export const UserProfileHeader = ({
  user,
  isEdit = false,
  showBackButton = false,
}: {
  user: UserInfo;
  isEdit?: boolean;
  showBackButton?: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentAccount = useDropboxAccount();
  const canEdit = canUserEditUserProfile(currentAccount?.email, user.email);
  const setUserProfileSettings = useSetAtom(userProfileSettingsAtom);
  const { reportPapEvent } = useMirageAnalyticsContext();
  const orgChart = useOrgChart();
  const person = usePersonFromOrgChart(user.uuid);

  const handleOnBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.state?.source) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleOnEditDoneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setUserProfileSettings(user.user_profile_settings); // reset to original settings
    reportPapEvent(
      PAP_Click_DashProfilePage({
        dashActionSurface: "dash_profile_page",
        dashProfileAction: "edit_done",
        featureLine: "people",
      }),
    );
    const profileHref = getUserProfilePageHref(user.display_handle);
    navigate(profileHref ? profileHref : "/");
  };

  const peopleBreadcrumbs =
    person && orgChart ? [...getReportingLine(person, orgChart)].reverse() : [];
  const showHeader = showBackButton || !!peopleBreadcrumbs.length || canEdit;
  if (!showHeader) {
    return null;
  }

  return (
    <Box as="div" className={styles.userProfileHeader}>
      <Box as="div" className={styles.userProfileHeaderLeft}>
        {showBackButton && (
          <Box marginRight="4">
            <NavLink to="#" onClick={handleOnBackClick}>
              <Button
                variant="borderless"
                withIconStart={<UIIcon src={ChevronLeftLine} />}
              >
                {i18n.t("back")}
              </Button>
            </NavLink>
          </Box>
        )}
        {!!peopleBreadcrumbs.length && (
          <PeopleBreadcrumbs
            employees={peopleBreadcrumbs.map(
              convertPeopleRecommendationToBasicUserInfo,
            )}
          />
        )}
      </Box>
      {canEdit && (
        <Box as="div" className={styles.userProfileHeaderRight}>
          {isEdit ? (
            <Split gap="8">
              <Split.Item>
                <Button variant="primary" onClick={handleOnEditDoneClick}>
                  {i18n.t("done")}
                </Button>
              </Split.Item>
            </Split>
          ) : (
            <Button
              variant="opacity"
              withIconStart={<UIIcon src={EditLine} />}
              onClick={() => {
                const editUrl = getEditUserProfilePageHref(user.display_handle);
                if (editUrl) {
                  reportPapEvent(
                    PAP_Click_DashProfilePage({
                      dashActionSurface: "dash_profile_page",
                      dashProfileAction: "edit",
                      featureLine: "people",
                    }),
                  );
                  navigate(editUrl);
                }
              }}
            >
              {i18n.t("edit")}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
